/**
 * MissionRitual — slice 5 of the My Day redesign.
 *
 * Wraps the Start Mission and Complete Mission actions with the
 * Set / Do / Close ritual screens from the design doc. The Do beat
 * is the existing habit / task / goal action UI (we deep-link to
 * it after Set fires); MissionRitual itself only renders the Set
 * and Close beats.
 *
 *   Set screen (mode = "set"):
 *     One line of context — title + pillar framing. Tap Begin →
 *     POST /api/missions/start, then deep-link to the underlying
 *     source. Non-skippable but deliberately tiny (5–10 seconds);
 *     the framing is what gives "Today's mission" weight over
 *     "highlighted habit."
 *
 *   Close screen (mode = "close"):
 *     POST /api/missions/complete, show "You showed up." + a
 *     visible pillar score nudge (the pillar bars invalidate so
 *     the next render shows the bump), then auto-dismiss after
 *     ~2 seconds. One signal — no confetti stack, no sound
 *     stack, per the brand-voice signal economy.
 *
 * Both Set and Close are Dialog primitives so they pause everything
 * underneath without page navigation — the ritual lives over the
 * existing surface, not as a separate route.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { openFocusMode } from "@/components/FocusMode";
import { PILLAR_COLOR } from "@/lib/pillar-colors";
import {
  ShieldCheck, TrendingUp, Eye, Play, Check, Loader2, Target,
} from "lucide-react";

type PillarKey = "integrity" | "momentum" | "clarity";
type SourceType = "habit" | "task" | "goal" | "vision";

interface MissionLite {
  source_type: SourceType;
  source_id: string;
  title: string;
  pillar: PillarKey;
}

const PILLAR_ICON: Record<PillarKey, typeof ShieldCheck> = {
  integrity: ShieldCheck,
  momentum: TrendingUp,
  clarity: Eye,
};

const PILLAR_LABEL: Record<PillarKey, string> = {
  integrity: "Integrity",
  momentum: "Momentum",
  clarity: "Clarity",
};

// One-line framing per pillar. Kept short on purpose — the Set beat
// is meant to be inhaled in a glance, not read.
const PILLAR_FRAMING: Record<PillarKey, string> = {
  integrity: "Your honesty work — showing UP when nobody's watching.",
  momentum: "Your consistency work — small reps compound.",
  clarity: "Your self-awareness work — naming what's true.",
};

export type MissionRitualMode = "set" | "close" | null;

interface MissionRitualProps {
  mode: MissionRitualMode;
  mission: MissionLite | null;
  onClose: () => void;
  /** Switches the MainApp tab (myday / habits / challenges / etc).
   *  Required for the deep-link handoff after Start Mission —
   *  habits / goals / vision all live under the habits tab. */
  onNavigateToTab?: (tabId: string) => void;
}

// sessionStorage key the habits tab reads on mount to scroll to a
// specific habit after a cross-tab navigation. Cleared by the
// consumer after use. Public so MyHabits can import it.
export const MISSION_FOCUS_HABIT_KEY = "trybeup_mission_focus_habit_id";

export default function MissionRitual({ mode, mission, onClose, onNavigateToTab }: MissionRitualProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();

  const startMut = useMutation({
    mutationFn: async () => {
      // No body — the route's `DateOnlyBody | None` parameter accepts
      // a missing body and falls back to the user's local today. An
      // empty `{}` body would FAIL validation (the required `date`
      // field is missing) and 422 silently — which was the original
      // "Start Mission does nothing" bug.
      const res = await apiRequest("POST", "/api/missions/start");
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${text ? `: ${text}` : ""}`);
      }
      return res.json();
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["/api/missions/today"] });
      const m = resp?.mission;

      // Focus Mode handoff (migration 121 + backend missions.py
      // enrichment 2026-06-19). When the source habit is tagged
      // focus_mode_enabled — Prayer, Quiet Time, etc., or anything in
      // Spiritual / Mental categories by default — open the
      // calming-timer surface instead of the habits-tab deep-link.
      // The active mission pill (`ActiveMissionBar`) still appears
      // afterwards; the user closes Focus Mode and lands back wherever
      // they were, with the pill confirming "you're in a session".
      // Founder report 2026-06-19: "tap Prayer → opens habits with a
      // confusing pill" — this handoff is the fix.
      if (m && resp?.focus_mode_enabled && resp?.habit) {
        openFocusMode({
          habitId: resp.habit.id,
          title: resp.habit.title,
          category: resp.habit.category,
          target: resp.habit.target,
          unit: resp.habit.unit,
          // From the mission Start beat — the Focus Mode complete
          // path also fires /api/missions/complete so the
          // ActiveMissionBar dismisses, instead of leaving the
          // "confusing purple pill" up after the timer ends.
          fromMission: true,
        });
        onClose();
        return;
      }

      if (m) {
        // Do beat handoff. The app is a single-route SPA at /app with
        // tabs managed inside MainApp — there are NO standalone
        // /habits, /goals, /vision routes. Earlier code called
        // navigate('/habits?focus=…') and 404'd. Use the parent's
        // onNavigateToTab to switch tabs, and stash the source_id in
        // sessionStorage so the destination tab (MyHabits) can scroll
        // to it on mount.
        switch (m.source_type as SourceType) {
          case "habit":
          case "goal":
          case "vision":
            try {
              sessionStorage.setItem(MISSION_FOCUS_HABIT_KEY, m.source_id);
            } catch { /* sessionStorage unavailable — no-op */ }
            if (onNavigateToTab) {
              onNavigateToTab("habits");
            } else {
              // Last-resort fallback: navigate to the app root with a
              // tab query param. MainApp's URL-param handler picks it
              // up. The void navigate is just to keep the import used
              // in case we add a non-tab route later.
              navigate(`/app?tab=habits`);
            }
            break;
          case "task":
            // Tasks live on My Day — no tab switch needed, just fire
            // the focus event so the task row scrolls into view.
            window.dispatchEvent(
              new CustomEvent("trybeup:focus-task", { detail: { id: m.source_id } })
            );
            break;
        }
      }
      onClose();
    },
    onError: (err) => {
      toast({
        title: "Couldn't start the mission",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    },
  });

  const [xpAwarded, setXpAwarded] = useState<number>(0);
  const completeMut = useMutation({
    mutationFn: async () => {
      // Same fix as startMut — no body so the backend uses
      // _user_today(current_user) for the date.
      const res = await apiRequest("POST", "/api/missions/complete");
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${text ? `: ${text}` : ""}`);
      }
      return res.json();
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["/api/missions/today"] });
      qc.invalidateQueries({ queryKey: ["/api/pillars/today"] });
      // Refresh the cached user so the level chip / XP counter
      // anywhere on My Day picks up the mission bonus.
      qc.invalidateQueries({ queryKey: ["/api/users/me"] });
      const awarded = typeof resp?.xp_awarded === "number" ? resp.xp_awarded : 0;
      setXpAwarded(awarded);
    },
    onError: (err) => {
      toast({
        title: "Couldn't mark complete",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    },
  });

  // Track whether we've fired the complete mutation yet — the close
  // beat fires it on open, then auto-dismisses after ~2s. Wrapping in
  // a ref-less flag avoids double-firing in StrictMode.
  const [completeFired, setCompleteFired] = useState(false);
  useEffect(() => {
    if (mode === "close" && !completeFired && mission) {
      setCompleteFired(true);
      completeMut.mutate();
    }
    if (mode !== "close") {
      setCompleteFired(false);
      setXpAwarded(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, mission]);

  // Auto-dismiss the close screen after 2.2s so the moment stays brief.
  useEffect(() => {
    if (mode !== "close") return;
    const t = window.setTimeout(() => {
      onClose();
    }, 2200);
    return () => window.clearTimeout(t);
  }, [mode, onClose]);

  const pillarFraming = useMemo(() => {
    if (!mission) return "";
    return PILLAR_FRAMING[mission.pillar];
  }, [mission]);

  if (!mode || !mission) return null;

  // Defensive fallback: if the backend ever ships a pillar value not
  // in our 3-key map, render a neutral Target icon instead of crashing
  // the whole card with "Element type is invalid: undefined." TS
  // protects us at compile-time but the API boundary is untyped.
  const PillarIcon = PILLAR_ICON[mission.pillar] ?? Target;
  // Per-pillar color via the shared style map — was always amber
  // before 2026-05-31, making every mission visually identical
  // regardless of which metric it belonged to.
  const pillarColor = PILLAR_COLOR[mission.pillar];

  // ── Set beat ─────────────────────────────────────────────────
  if (mode === "set") {
    return (
      <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent
          className="max-w-sm"
          data-testid="mission-ritual-set"
        >
          <DialogTitle className="sr-only">Today's mission</DialogTitle>
          <DialogDescription className="sr-only">
            Brief context before you begin.
          </DialogDescription>

          <div className="text-center py-2 space-y-4">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${pillarColor.iconBg} mx-auto`}>
              <PillarIcon className={`w-7 h-7 ${pillarColor.iconText}`} strokeWidth={1.75} />
            </div>
            <div>
              {/* Merged eyebrow — "TODAY'S MISSION · ⚡ MOMENTUM" — same
                  rhythm the MissionCard pending/completed states use, so
                  the ritual modal reads as a continuation of the card not
                  a separate language. Drops the redundant "Momentum
                  pillar" sub-line below the framing since the pillar is
                  now named in the eyebrow itself. */}
              <p className={`inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${pillarColor.text} mb-2`}>
                <span>Today's mission</span>
                <span className="opacity-50">·</span>
                <PillarIcon className="w-3 h-3" strokeWidth={2} />
                <span>{PILLAR_LABEL[mission.pillar]}</span>
              </p>
              <h2
                className="text-2xl font-bold text-foreground leading-tight"
              >
                {mission.title}
              </h2>
              <p className="text-sm text-muted-foreground mt-3">
                {pillarFraming}
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => startMut.mutate()}
              disabled={startMut.isPending}
              className="bg-primary hover:bg-primary text-primary-foreground w-full"
              data-testid="mission-ritual-begin"
            >
              {startMut.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1.5" strokeWidth={2.5} />
                  Begin
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Close beat ───────────────────────────────────────────────
  // Fires the complete mutation on open, shows the grounded line +
  // a tiny pillar-bump animation, auto-dismisses.
  //
  // Brand-voice nuance: integrity-pillar missions (mostly work_on
  // habits, where the user is reducing or naming something hard)
  // are NOT celebrations — they're honest logs. Soften the headline
  // when the mission's pillar is integrity. The pillar inference
  // tags work_on habits as integrity (see mission_service.py), so
  // this branch catches them cleanly without needing tracking_type
  // on the mission payload.
  const isHonestLog = mission.pillar === "integrity";
  const closeHeadline = isHonestLog ? "You named it." : "You showed up.";
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-sm"
        data-testid="mission-ritual-close"
      >
        <DialogTitle className="sr-only">Mission complete</DialogTitle>
        <DialogDescription className="sr-only">
          A quiet acknowledgement.
        </DialogDescription>

        <div className="text-center py-6 space-y-4">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 mx-auto animate-pulse-once"
            style={{ animation: "pillarPulse 1.2s ease-out 1" }}
          >
            <Check className="w-8 h-8 text-emerald-400" strokeWidth={2.5} />
          </div>
          <div>
            <h2
              className="text-2xl font-bold text-foreground leading-tight"
            >
              {closeHeadline}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              {mission.title}
            </p>
            {/* +Pillar credit, as a soft chip. Used to be flat text on
                a transparent background; now sits inside a pillar-tinted
                pill so the "you earned a Momentum point" beat reads as a
                bestowed thing, not an inline label. */}
            <div className="flex items-center justify-center mt-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${pillarColor.iconBg}`}>
                <PillarIcon className={`w-3 h-3 ${pillarColor.iconText}`} strokeWidth={2.25} />
                <span className={`text-[11px] uppercase tracking-[0.18em] ${pillarColor.text} font-semibold`}>
                  +{PILLAR_LABEL[mission.pillar]}
                </span>
              </span>
            </div>
            {xpAwarded > 0 && (
              <p
                className="text-[11px] text-muted-foreground mt-2"
                data-testid="mission-ritual-xp"
              >
                +{xpAwarded} XP
              </p>
            )}
          </div>
        </div>

        {/* Inline keyframes for the one-time pillar pulse. Kept here
            rather than in tailwind config so it's local to this
            component and doesn't bloat the global stylesheet. */}
        <style>{`
          @keyframes pillarPulse {
            0% { transform: scale(0.85); opacity: 0; }
            40% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
